/**
 * Modal — 포털 기반 다이얼로그 컴포넌트
 *
 * - createPortal로 document.body에 렌더링
 * - 백드롭 클릭 또는 ESC 키로 닫힘
 * - open=false일 때 DOM에서 완전히 제거됨
 */

import { ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/cn.js'
import styles from './Modal.module.css'

interface ModalProps {
  /** 모달 열림 여부 */
  open: boolean
  /** 닫기 요청 콜백 (백드롭 클릭 / ESC) */
  onClose: () => void
  /** 헤더 타이틀 텍스트 */
  title?: string
  /** 헤더 우측 추가 요소 (닫기 버튼 왼쪽에 배치) */
  headerExtra?: ReactNode
  /** 모달 너비 변형. 기본값 md */
  size?: 'sm' | 'md' | 'lg'
  className?: string
  children: ReactNode
}

export function Modal({ open, onClose, title, headerExtra, size = 'md', className, children }: ModalProps) {
  // ESC 키 이벤트 등록
  useEffect(() => {
    if (!open) return

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  // 스크롤 잠금
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* 패널 클릭 시 이벤트 전파 차단 */}
      <div
        className={cn(styles.panel, styles[size], className)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          {title ? <h2 className={styles.title}>{title}</h2> : <div />}
          <div className={styles.headerRight}>
            {headerExtra}
            <button className={styles.closeBtn} onClick={onClose} aria-label="닫기">
              ✕
            </button>
          </div>
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </div>,
    document.body,
  )
}
